import classNames from "classnames";

interface SkeletonLoaderProps {
  /** Additional wrapper class for layout control */
  wrapperClass?: string;
  /** Border radius (Tailwind classes or custom CSS) */
  borderRadius?: string;
  /** Additional CSS classes */
  className?: string;
  /** Number of skeleton items to render (for lists) */
  count?: number;
}

/**
 * A reusable skeleton loader component that displays a shimmer animation
 * while content is loading.
 *
 * @example
 * // Single skeleton with custom height
 * <SkeletonLoader className="h-10" />
 *
 * @example
 * // Multiple skeletons with custom dimensions
 * <SkeletonLoader className="w-64 h-12" count={3} />
 *
 * @example
 * // Custom styling with rounded corners
 * <SkeletonLoader className="w-full h-8" borderRadius="rounded-lg" />
 */
const SkeletonLoader = ({
  borderRadius = "rounded",
  className,
  count = 1,
  wrapperClass = "",
}: SkeletonLoaderProps) => {
  const skeletonClass = classNames(
    "bg-gray-300 skeleton-shimmer",
    borderRadius,
    className,
  );

  if (count === 1) {
    return <div className={skeletonClass} />;
  }

  return (
    <div className={classNames("flex flex-col", wrapperClass)}>
      {Array.from({ length: count }, (_, index) => (
        <div key={index} className={skeletonClass} />
      ))}
    </div>
  );
};

export default SkeletonLoader;
